import { c as _c } from "react/compiler-runtime";
/**
 * React-scan toggle section of the DevPill. Extracted from dev-pill.tsx
 * as part of Phase 6 of the round-3 enactment plan
 * (review-9016e0cc-83b #7). Only consumed by dev-pill.tsx's PillUI.
 */
import { Bell, Radar } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactScanAdapter } from '../adapters';

/** Inline style applied to the Bell icon to visually de-emphasize the render-count indicator. */
const BELL_STYLE = {
  opacity: 0.5
} as const;

/** State and controls for the react-scan overlay section of the DevPill. */
export interface ScanSectionState {
  visible: boolean;
  fps: number | null;
  renderCount: number | null;
  toggle: () => void;
  /** True when on a touch device and react-scan hasn't been loaded yet. */
  disabled: boolean;
}

/** Manages react-scan visibility, live FPS sampling, and render-count polling; handles deferred loading on touch devices. */
export function useScanSection(adapter: ReactScanAdapter, ready: boolean): ScanSectionState {
  const [visible, setVisible] = useState(false);
  const [fps, setFps] = useState<number | null>(null);
  const [renderCount, setRenderCount] = useState<number | null>(null);
  const [isMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches);
  const [mobileReady, setMobileReady] = useState(false);
  const effectiveReady = ready || mobileReady;
  useEffect(() => {
    if (!effectiveReady || !visible) return;
    let cancelled = false;
    function measure() {
      if (cancelled) return;
      let count = 0;
      const start = performance.now();
      function frame() {
        if (cancelled) return;
        count++;
        if (performance.now() - start < 1000) {
          requestAnimationFrame(frame);
        } else {
          setFps(count);
        }
      }
      requestAnimationFrame(frame);
    }

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change -- FPS measurement via requestAnimationFrame is an external system subscription
    measure();
    const id = setInterval(measure, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [effectiveReady, visible]);
  useEffect(() => {
    if (!effectiveReady) return;
    function poll() {
      setRenderCount(adapter.getRenderCount());
    }
    poll();
    const id_0 = setInterval(poll, 5000);
    return () => clearInterval(id_0);
  }, [effectiveReady, adapter]);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = useRef(false);
  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      if (retryTimeoutRef.current != null) clearTimeout(retryTimeoutRef.current);
    };
  }, []);
  const toggle = useCallback(() => {
    // Mobile: load react-scan on first tap
    if (isMobile && !effectiveReady) {
      const loader = (globalThis as Record<string, unknown>)['__loadReactScan'] as ((cb: () => void) => void) | undefined;
      if (!loader) return;
      loader(() => {
        let attempts = 0;
        function tryDiscover() {
          if (unmountedRef.current) return;
          if (adapter.discover()) {
            setMobileReady(true);
            setVisible(true);
          } else if (++attempts < 50) {
            retryTimeoutRef.current = setTimeout(tryDiscover, 100);
          }
        }
        tryDiscover();
      });
      return;
    }
    if (adapter.isVisible()) {
      adapter.hide();
      setVisible(false);
    } else {
      adapter.show();
      setVisible(true);
    }
  }, [adapter, isMobile, effectiveReady]);
  return {
    visible,
    fps,
    renderCount,
    toggle,
    disabled: isMobile && !effectiveReady
  };
}

/** Renders the react-scan toggle button, displaying live FPS and render-count stats when the overlay is visible. */
export function ScanSection(t0) {
  const $ = _c(13);
  const {
    section
  } = t0;
  const t1 = `section${section.visible ? " active" : ""}`;
  const t2 = section.disabled ? "scan-off" : undefined;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Radar size={14} />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== t2) {
    t4 = <span className={t2}>{t3}</span>;
    $[1] = t2;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== section.fps) {
    t5 = section.fps != null && <span className="scan-stat"><span className="scan-value">{section.fps}</span>fps</span>;
    $[3] = section.fps;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== section.renderCount) {
    t6 = section.renderCount != null && <span className="scan-stat"><Bell size={10} style={BELL_STYLE} /><span className="scan-value">{section.renderCount}</span></span>;
    $[5] = section.renderCount;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== section.toggle || $[8] !== t1 || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = <button type="button" className={t1} onClick={section.toggle}>{t4}{t5}{t6}</button>;
    $[7] = section.toggle;
    $[8] = t1;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
}